import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import 'regenerator-runtime/runtime';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import { getLocale } from '@libs/sembo-dates';
import { decodeHTML, measureInitializationPerformance } from '@libs/utils';
import {
  assignExperimentValue,
  getExperiment,
  initializeStatsig,
  trackPerformance,
  trackPerformanceAppInitiated,
  trackPerformancePageLoaded,
} from '@libs/feature-toggle';

import App from './App.vue';
import { addCustomDirectives } from './directives';
import './styles/main.scss';
import { createLogger } from './modules/plugins/logger';
import { languageLocale } from './modules/localization/localeUtils';

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

const initializationPerformanceMeasurement = measureInitializationPerformance();
trackPerformance();

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

(async function (): Promise<void> {
  await initializeStatsig();

  initializationPerformanceMeasurement.then(
    ({ timeToAppInitiatedMs, timeToPageLoadedMs }) => {
      trackPerformancePageLoaded(timeToPageLoadedMs);
      trackPerformanceAppInitiated(timeToAppInitiatedMs);
    }
  );

  const isSimplifiedMobileSummaryExperiment = getExperiment(
    'simplified_mobile_summary'
  ).get('should_show_simplified_mobile_summary', false);
  assignExperimentValue(
    'simplified_mobile_summary',
    isSimplifiedMobileSummaryExperiment
  );

  const isHotelDetailsRoomCardsGridLayoutExperiment = getExperiment(
    'hotel_details_room_cards_grid_layout'
  ).get('hotel_details_room_cards_grid_layout', false);
  assignExperimentValue(
    'hotel_details_room_cards_grid_layout',
    isHotelDetailsRoomCardsGridLayoutExperiment
  );

  const isCalendarWithoutPricesInCarTripExperiment = getExperiment(
    'calendar_without_prices_in_car_trip'
  ).get('should_show_calendar_without_prices_in_car_trip', false);
  assignExperimentValue(
    'calendar_without_prices_in_car_trip',
    isCalendarWithoutPricesInCarTripExperiment
  );

  const isRemoveBulletPointsHotelCardExperiment = getExperiment(
    'remove_bullet_points_hotel_card'
  ).get('should_remove_bullet_points', false);
  assignExperimentValue(
    'remove_bullet_points_hotel_card',
    isRemoveBulletPointsHotelCardExperiment
  );

  const isMoreDatesAndPricesCopyInHotelSelectionExperiment = getExperiment(
    'more_dates_and_prices_copy_in_hotel_selection'
  ).get('should_show_more_dates_and_prices_copy_in_hotel_selection', false);
  assignExperimentValue(
    'more_dates_and_prices_copy_in_hotel_selection',
    isMoreDatesAndPricesCopyInHotelSelectionExperiment
  );
  const isOptimizedFlihtAdditionInTransportBannerExperiment = getExperiment(
    'optimized_flight_addition_in_transport_banner'
  ).get('should_show_optimized_flight_addition_in_transport_banner', false);
  assignExperimentValue(
    'optimized_flight_addition_in_transport_banner',
    isOptimizedFlihtAdditionInTransportBannerExperiment
  );

  const isNewBookTypeButtonExperiment = getExperiment(
    'new_book_type_button'
  ).get('should_show_new_book_type_button', false);
  assignExperimentValue('new_book_type_button', isNewBookTypeButtonExperiment);

  const isMetaTripOverviewHotelCardExperiment = getExperiment(
    'to_meta_hotel_card'
  ).get('should_show_new_book_type_button', false);
  assignExperimentValue(
    'to_meta_hotel_card',
    isMetaTripOverviewHotelCardExperiment
  );
  const isMobileFlightHeaderExperiment = getExperiment(
    'mobile_flight_header'
  ).get('should_see_mobile_flight_header', false);
  assignExperimentValue('mobile_flight_header', isMobileFlightHeaderExperiment);
})();

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

addCustomDirectives(app);

setDefaultOptions({
  locale: getLocale(languageLocale),
});

loadLanguageAsync(languageLocale);
